import axios from "axios";
import { get_headers, CIPC_VERIFICATION_URL, CIPC_VERIFIED_DATA } from "@/utils/constants/constants.js";

/**
 * Detects if current context is lead or application
 * @returns {Object} Context type and ID
 */
function getVerificationContext() {
  const application_form_id = sessionStorage.getItem("application_form_id");
  const lead_form_id = sessionStorage.getItem("lead_form_id");
  const lead_id = sessionStorage.getItem("lead_id");

  if (lead_form_id || lead_id) {
    return { 
      type: 'lead', 
      lead_form_id: lead_form_id || null,
      lead_id: lead_id || null 
    };
  }
  
  return { 
    type: 'application', 
    application_form_id: application_form_id || null 
  };
}

/**
 * Parses error responses from Axios and returns a user-friendly message.
 * @param {Object} error - Axios error object
 * @param {string} fallbackMessage - Default message if no specific error is found
 * @returns {string} - Parsed error message
 */

/**
 * Format value for display
 */
function format_value(value) {
  if (value === null || value === undefined) return "N/A";
  if (Array.isArray(value) && value.length === 0) return "No data available";
  return value;
}

/**
 * Flatten nested objects for display
 */
function flatten_cipc_data(data) {
  const result = {};
  const stack = [{ obj: data, prefix: "" }];

  while (stack.length) {
    const { obj, prefix } = stack.pop();
    for (const key of Object.keys(obj || {})) {
      const value = Reflect.get(obj, key);
      const new_key = prefix ? `${prefix}_${key}` : key;
      if (value && typeof value === "object" && !Array.isArray(value)) {
        stack.push({ obj: value, prefix: new_key });
      } else {
        Reflect.set(result, new_key, format_value(value));
      }
    }
  }

  return result;
}

/**
 * Safely get nested value from object
 */
function safe_get(obj, path, fallback = "N/A") {
  if (!obj) return fallback;
  const keys = Array.isArray(path) ? path : path.split(".");
  let value = obj;
  for (const key of keys) {
    if (!value || !(key in value)) return fallback;
    value = Reflect.get(value, key);
  }
  return value ?? fallback;
}

/**
 * Extract multiple fields from object based on a map
 */
function extract_fields(data, field_map) {
  const output = {};
  for (const [new_key, path] of Object.entries(field_map)) {
    Reflect.set(output, new_key, safe_get(data, path));
  }
  return output;
}

/**
 * Try parse JSON safely
 */
function try_parse_json(value) {
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return value; }
}

/**
 * Parse and structure CIPC results data
 */
function parse_cipc_results_data(raw_data) {
  if (!raw_data) return {};

  const results = normalise_results(raw_data);
  const commercial = results?.Commercial;

  if (!commercial) {
    return flatten_cipc_data(results);
  }

  return extractCommercialData(commercial);
}

// ---------------------- Helpers ----------------------

function normalise_results(raw) {
  const keys = "results_data,data,details".split(",");
  let value = find_first_available(raw, keys) ?? raw;

  const parsed = try_parse_json(value);

  return unwrap_results_array(parsed);
}

function find_first_available(source, keys) {
  for (const key of keys) {
    const val = Reflect.get(source, key);
    if (val !== undefined && val !== null) return val;
  }
  return null;
}

function unwrap_results_array(obj) {
  const results = Reflect.get(obj, "results");
  if (Array.isArray(results)) {
    return Reflect.get(results, 0) ?? obj;
  }
  return obj;
}




function extractCommercialData(commercial) {
  const field_map = getFieldMap();
  return extract_fields(commercial, field_map);
}

function getFieldMap() {
  return {
    // Commercial Scoring
    band: "CommercialScoring.Band",
    score_date: "CommercialScoring.ScoreDate",
    final_score: "CommercialScoring.FinalScore",
    display_text: "CommercialScoring.DisplayText",
    commercial_id: "CommercialScoring.CommercialID",
    exception_code: "CommercialScoring.ExceptionCode",
    score_band_range_1: "CommercialScoring.ScoreBandRange1",
    score_band_range_2: "CommercialScoring.ScoreBandRange2",
    score_band_range_3: "CommercialScoring.ScoreBandRange3",
    score_band_range_4: "CommercialScoring.ScoreBandRange4",
    score_band_range_5: "CommercialScoring.ScoreBandRange5",
    band_message_1: "CommercialScoring.Band_x002F_Message1",
    band_message_2: "CommercialScoring.Band_x002F_Message2",
    band_message_3: "CommercialScoring.Band_x002F_Message3",
    band_message_4: "CommercialScoring.Band_x002F_Message4",
    band_message_5: "CommercialScoring.Band_x002F_Message5",

    // Simple fields
    report_information: "ReportInformation",
    business_enquiry_history: "BusinessEnquiryHistory",
    subscriber_input_details: "SubscriberInputDetails",
    commercial_business_information: "CommercialBusinessInformation",
    commercial_auditor_information: "CommercialAuditorInformation",
    commercial_principal_information: "CommercialPrincipalInformation",
    director_current_business_interests: "DirectorCurrentBusinessinterests",
    active_principal_info_summary: "CommercialActivePrincipalInfoSummary",
    active_principal_information: "CommercialActivePrincipalInformation",
    active_director_business_interests: "ActiveDirectorCurrentBusinessinterests",
    inactive_principal_info_summary: "CommercialInActivePrincipalInfoSummary"
  };
}

/**
 * Build query parameters for CIPC verification
 * @param {Object} params - Input parameters
 * @returns {Object} Query parameters or error object
 */
function buildQueryParams(params) {
  if (!params.service_id) {
    return {
      error: true,
      response: {
        status: "error",
        message: "Service ID is required",
        results: [],
        service_name: "CIPC Verification",
      }
    };
  }

  const query_params = { service_id: params.service_id };

  if (params.entity_profile_id) {
    query_params.entity_profile_id = params.entity_profile_id;
  } else if (params.lead_id) {
    query_params.lead_id = params.lead_id;
  } else {
    const entity_id = sessionStorage.getItem("entity_profile_id");
    const lead_id = sessionStorage.getItem("lead_id");
    
    if (entity_id) {
      query_params.entity_profile_id = entity_id;
    } else if (lead_id) {
      query_params.lead_id = lead_id;
    } else {
      return {
        error: true,
        response: {
          status: "error",
          message: "Either entity_profile_id or lead_id is required",
          results: [],
          service_name: "CIPC Verification",
        }
      };
    }
  }

  return { error: false, query_params };
}

/**
 * Normalize verification status
 * @param {string} status - Raw status from API
 * @returns {string} Normalized status ('success' or 'fail')
 */
function normalizeStatus(status) {
  const status_text = (status || "").toLowerCase();
  return status_text.includes("success") ||
         status_text.includes("pass") ||
         status_text.includes("verified") ||
         status_text.includes("completed")
    ? "success"
    : "fail";
}

/**
 * Format successful verification response
 * @param {Object} data - API response data
 * @returns {Object} Formatted response
 */
function formatSuccessResponse(data) {
  const normalized_status = normalizeStatus(data.status);
  const default_message = normalized_status === "fail" ? "System verification failed." : "";
  const parsed_results_data = parse_cipc_results_data(data);

  const result_object = {
    status: normalized_status,
    message: data.message || default_message,
    field_results: data.field_results || {},
    results_data: parsed_results_data,
    service_name: "CIPC Verification",
  };

  return {
    status: normalized_status,
    results: [result_object],
    raw_data: data,
    service_name: "CIPC Verification",
  };
}

/**
 * Format error response
 * @param {Object} data - Error data from API
 * @returns {Object} Formatted error response
 */
function formatErrorResponse(data) {
  return {
    status: "error",
    message: data.message,
    results: [{
      status: "error",
      message: data.message,
      field_results: {},
      results_data: {},
      service_name: "CIPC Verification",
    }],
    raw_data: data,
    service_name: "CIPC Verification",
  };
}

/**
 * CIPC Verification API function
 * @param {Object} payload - Contains service_id, form_id, context
 * @returns {Promise} - API response
 */
export const cipcVerify = async (payload) => {
  const headers = get_headers();
  
  const context = getVerificationContext();
  const enrichedPayload = {
    ...payload,
    ...context
  };

  const response = await axios.post(CIPC_VERIFICATION_URL, enrichedPayload, { headers });
  return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
};

/**
 * Get CIPC Verification Results
 * @param {Object} params - Contains service_id AND either entity_profile_id OR lead_id
 * @returns {Promise} - API response with verification results
 */
export const getCIPCVerificationResults = async (params = {}) => {
  const headers = get_headers();

  try {
    const queryResult = buildQueryParams(params);
    if (queryResult.error) {
      return queryResult.response;
    }

    const response = await axios.get(CIPC_VERIFIED_DATA, {
      headers,
      params: queryResult.query_params,
    });

    const data = typeof response.data === "string" ? JSON.parse(response.data) : response.data;

    if (data.status === "error") {
      return formatErrorResponse(data);
    }

    return formatSuccessResponse(data);
  } catch (error) {
    return {
      status: "error",
      message:
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch verification results",
      results: [],
      raw_data: null,
      service_name: "CIPC Verification",
    };
  }
};